import { ref } from "vue"
import api from "./useApi"
import { url } from "../helper"
import { useCurrentUser } from "./useCurrentUser"
import { useI18n } from "vue-i18n"
import { useUserStore } from "../stores/user"
import { refreshTokenIfNeeded } from "./useAuth"

export const useUserProfiles = () => {
	const loading = ref(false)
	const error = ref<string | null>(null)
	const { locale } = useI18n()
	const userStore = useUserStore()

	const getJwtPayloadSafe = (): any | null => {
		const token = localStorage.getItem("jwt")
		if (!token) return null
		try {
			const base64 = token.split(".")[1]
			if (!base64) return null
			return JSON.parse(atob(base64))
		} catch {
			return null
		}
	}

	const createProfile = async (profileData: { [key: string]: any }): Promise<string> => {
		try {
			const response = await api.post(
				`${url}/jsonapi/profile/subscriber_profile`,
				{
					data: {
						type: "profile--subscriber_profile",
						attributes: profileData.attributes,
						relationships: profileData.relationships || {},
					},
				},
				{
					headers: {
						"Content-Type": "application/vnd.api+json",
						Accept: "application/vnd.api+json",
					},
				}
			)
			return response.data.data.id
		} catch (err) {
			throw new Error("Failed to create profile")
		}
	}

	const getCurrentProfileIds = async (): Promise<string[]> => {
		try {
			const payload = getJwtPayloadSafe()
			if (!payload?.uuid) return []
			const response = await api.get(
				`${url}/jsonapi/user/user/${payload.uuid}?jsonapi_include=1&include=subscriber_profile_profiles,subscriber_profile_profiles.field_profile_photo,subscriber_profile_profiles.field_profile_photo.field_media_image`,
				{
					headers: {
						Accept: "application/vnd.api+json",
					},
				}
			)
			const user = response.data.data
			const key = "subscriber_profile_profiles"
			if (!key) return []
			const profiles = user.relationships?.[key]?.data || []
			return profiles.map((p: any) => p.id)
		} catch {
			return []
		}
	}

	const updateUserProfiles = async (userId: string, profileIds: string[]) => {
		try {
			const key = "subscriber_profile_profiles"
			await api.patch(
				`${url}/jsonapi/user/user/${userId}`,
				{
					data: {
						type: "user--user",
						id: userId,
						relationships: {
							[key]: {
								data: profileIds.map((id) => ({
									type: "profile--subscriber_profile",
									id,
								})),
							},
						},
					},
				},
				{
					headers: { "Content-Type": "application/vnd.api+json", Accept: "application/vnd.api+json" },
				}
			)
		} catch {
			throw new Error("Failed to update user profiles")
		}
	}
	/**
	 * This function adds a new profile to the current user
	 * @param profileData This is the data of the profile to be added
	 * @returns Returns true if the profile was added successfully, false otherwise
	 */
	const addProfileToUser = async (profileData: { [key: string]: any }) => {
		loading.value = true
		error.value = null

		try {
			const payload = getJwtPayloadSafe()
			if (!payload?.uuid) throw new Error("Missing user UUID")
			const existingIds = await getCurrentProfileIds()

			if (existingIds.length >= 3) {
				throw new Error("You have reached the maximum number of profiles")
			}

			const newProfileId = await createProfile(profileData)
			localStorage.setItem("user_profile", newProfileId)

			const updatedProfileIds = [...existingIds, newProfileId]
			await updateUserProfiles(payload.uuid, updatedProfileIds)
			localStorage.removeItem("jwt")
			await refreshTokenIfNeeded()
			// await attachProfileToUser(payload.uuid, newProfileId)
			const { fetchCurrentUser } = useCurrentUser(locale)
			await fetchCurrentUser()

			return true
		} catch (err: any) {
			error.value = err.message || "An error occurred"
			return false
		} finally {
			loading.value = false
		}
	}
	/**
	 * With this function we can get from backend the video_flag entities for a given profile and parameters
	 * @param movieUuid It is the item Uuid can be undefined if fetching for series/episode
	 * @param entityType This is the type of the entity (movie, episode, etc)
	 * @param flag This is the flag of the entity (started, completed, favotite)
	 * @param parentUuid This is the parent entity uuid, needed for episode/series
	 * @returns It returns the video_flag entities for the given profile and parameters
	 */
	const fetchVideoFlagStoreByProfile = async (
		movieUuid: string | undefined,
		entityType: string,
		flag: string | undefined,
		parentUuid: string | undefined
	) => {
		// Checking series and episodes
		if (parentUuid !== undefined) {
			const params: any = {
				jsonapi_include: "1",
				include: "target_entity",
				"filter[target_entity_type]": entityType,
				"filter[profile_id]": userStore.state.selected_profile.id,
				"filter[video_flag_type]": flag,
				"filter[parent_entity.id]": parentUuid,
				sort: "-changed",
			}

			if (movieUuid !== undefined) {
				params["filter[target_entity.id]"] = movieUuid
			}

			const response = await api.get(`/jsonapi/video_flag/video_flag`, {
				params,
				headers: {
					Accept: "application/vnd.api+json",
				},
			})
			return response.data.data
		}
		// checking movies
		else {
			const response = await api.get(`/jsonapi/video_flag/video_flag`, {
				params: {
					jsonapi_include: "1",
					include: "target_entity",
					"filter[target_entity.id]": movieUuid,
					"filter[target_entity_type]": entityType,
					"filter[profile_id]": userStore.state.selected_profile.id,
					"filter[video_flag_type]": flag,
					sort: "-changed",
					"page[limit]": 1,
				},
				headers: {
					Accept: "application/vnd.api+json",
				},
			})
			return response.data.data
		}
	}
	/**
	 * With this function we can delete an entity in video_flag table
	 * @param itemUuid this is the uuid of the custom entity in video_flag table
	 * @returns
	 */
	const deleteVideoFlag = async (itemUuid: string) => {
		return api.delete(`/jsonapi/video_flag/video_flag/${itemUuid}`, {
			headers: {
				"Content-Type": "application/vnd.api+json",
				Accept: "application/vnd.api+json",
			},
		})
	}

	/**
	 * Creates an entity in video_flag table
	 * @param flag
	 * @param entityType
	 * @param timestamp
	 * @param movieUuid
	 * @returns
	 */
	const createVideoFlag = async (
		flag: string,
		entityType: string,
		timestamp: number | undefined,
		itemUuid: string,
		parentId: string | undefined
	) => {
		if (entityType === "episode") {
			const payload = getJwtPayloadSafe()
			if (!payload?.uuid) return []
			return api.post(
				"/jsonapi/video_flag/video_flag",
				{
					data: {
						type: "video_flag--video_flag",
						attributes: {
							video_flag_type: flag,
							profile_id: userStore.state.selected_profile.id,
							target_entity_type: entityType,
							timestamp: timestamp,
						},
						relationships: {
							uid: {
								data: {
									type: "user--user",
									id: payload.uuid,
								},
							},
							target_entity: {
								data: {
									type: "node--episode",
									id: itemUuid,
								},
							},
							parent_entity: {
								data: {
									type: "node--series",
									id: parentId, // To be filled if needed
								},
							},
						},
					},
				},
				{
					headers: {
						"Content-Type": "application/vnd.api+json",
						Accept: "application/vnd.api+json",
					},
				}
			)
		} else if (entityType === "series") {
			const payload = getJwtPayloadSafe()
			return api.post(
				"/jsonapi/video_flag/video_flag",
				{
					data: {
						type: "video_flag--video_flag",
						attributes: {
							video_flag_type: flag,
							profile_id: userStore.state.selected_profile.id,
							target_entity_type: entityType,
							timestamp: timestamp,
						},
						relationships: {
							uid: {
								data: {
									type: "user--user",
									id: payload.uuid,
								},
							},
							target_entity: {
								data: {
									type: "node--series",
									id: itemUuid,
								},
							},
						},
					},
				},
				{
					headers: {
						"Content-Type": "application/vnd.api+json",
						Accept: "application/vnd.api+json",
					},
				}
			)
		} else {
			const payload = getJwtPayloadSafe()
			if (!payload?.uuid) return []
			return api.post(
				"/jsonapi/video_flag/video_flag",
				{
					data: {
						type: "video_flag--video_flag",
						attributes: {
							video_flag_type: flag,
							profile_id: userStore.state.selected_profile.id,
							target_entity_type: entityType,
							timestamp: timestamp,
						},
						relationships: {
							uid: {
								data: {
									type: "user--user",
									id: payload.uuid,
								},
							},
							target_entity: {
								data: {
									type: "node--movie",
									id: itemUuid,
								},
							},
						},
					},
				},
				{
					headers: {
						"Content-Type": "application/vnd.api+json",
						Accept: "application/vnd.api+json",
					},
				}
			)
		}
	}

	/**
	 *
	 * @param flag
	 * @param timestamp new value for timestamp
	 * @param itemUuid this is the uuid of the custom entity in video_flag table
	 * @returns
	 */
	const updateVideoFlagStore = async (flag: string, timestamp: number | undefined, itemUuid: string) => {
		return api.patch(
			`/jsonapi/video_flag/video_flag/${itemUuid}`,
			{
				data: {
					type: "video_flag--video_flag",
					id: itemUuid,
					attributes: {
						timestamp: timestamp,
						video_flag_type: flag,
					},
				},
			},
			{
				headers: {
					"Content-Type": "application/vnd.api+json",
					Accept: "application/vnd.api+json",
				},
			}
		)
	}

	/**
	 * Starts process to create or update an entity in video_flag table
	 * @param flag this is the flag of the entity (started, completed, favotite)
	 * @param type this is the type of the entity (movie, episode, etc)
	 * @param timestamp this is the timestamp of the event in seconds
	 * @param itemUuid this is the uuid of the entity (movie, episode, etc)
	 */
	const storeStartedEntity = async (
		flag: string,
		type: string,
		timestamp: number | undefined,
		itemUuid: string,
		parentId: string | undefined
	) => {
		const userStore = useUserStore()
		const existingStores = ref([])

		// 1. Check if playback_store already exists for this profile
		if (flag === "completed") {
			// to mark an entity as completed we have to find an entity with flag started
			existingStores.value = await fetchVideoFlagStoreByProfile(itemUuid, type, "started", parentId)
		} else {
			existingStores.value = await fetchVideoFlagStoreByProfile(itemUuid, type, flag, parentId)
		}

		if (existingStores.value.length > 0) {
			// 2. Update existing record
			if ("favourite" === flag) {
				await deleteVideoFlag(existingStores.value[0]?.id)
				return "deleted"
			} else {
				await updateVideoFlagStore(flag, timestamp, existingStores.value[0]?.id)
				return "updated"
			}
		} else {
			// 3. Create new record
			await createVideoFlag(flag, type, timestamp, itemUuid, parentId)
			return "created"
		}
	}

	return {
		addProfileToUser,
		storeStartedEntity,
		fetchVideoFlagStoreByProfile,
		loading,
		error,
	}
}
